// NPM Imports
import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Input, Toastr } from "@revvsales/components";

// CSS Imports
import signIn from "./signin.module.scss";

// Component Imports
import GoogleAuth from "../GoogleAuth";
import SAMLAuth from "../SAMLAuth";
import SignupQesWrapper from "../SignUp/SignupQesWrapper";

// Service Imports
import {
  verifyInviteSecretCode,
  triggerInviteSecretCode,
} from "../../AuthService/authService";

function PendingInviteForm(props) {
  const {
    data: { email, invitedDomains, selectedDomain, suggestion },
  } = props;

  const domain = invitedDomains.find(
    item => item.sub_domain === selectedDomain,
  );

  const isDomainPreferenceSaml = domain.preference === "s";

  // Local States
  const [state, setState] = useState({
    errMsg: "",
    inviteCode: "",
    secretCode: "",
    showSignUpForm: false,
    preference: "",

    // Google Auth Related States
    fname: "",
    lname: "",
    googleToken: "",
  });

  const _onSecretCodeInputChange = e => {
    let trimedValue = e.target.value;
    if (trimedValue) {
      trimedValue = trimedValue.trim();
    }
    setState(prev => ({ ...prev, secretCode: trimedValue, errMsg: "" }));
  };

  const _onGoBackClick = () => {
    props.toggleView({
      currentView: "orgListing",
      isVerified: true,
      selectedDomain: "",
    });
  };

  const _validateSecretCode = () => {
    if (state.secretCode && state.secretCode.length === 6) {
      const payload = {
        user_email: email,
        sub_domain: domain.sub_domain,
        invite_code: state.secretCode,
      };

      return verifyInviteSecretCode(payload)
        .then(response => {
          setState(prev => ({
            ...prev,
            errMsg: "",
            inviteCode: response.invite_code,
            preference: "p",
            showSignUpForm: true,
          }));
        })
        .catch(err => {
          console.error(
            `PendingInviteForm => _validateSecretCode => verifyInviteSecretCode => ${err}`,
          );
          setState(prev => ({
            ...prev,
            errMsg: "Invalid secret code!",
          }));
        });
    }
    return setState(prev => ({
      ...prev,
      errMsg: "Enter valid secret code!",
    }));
  };

  const _reTriggerInviteSecretCode = () => {
    const payload = {
      user_email: email,
      sub_domain: domain.sub_domain,
      invite_by: domain.invited_by_id,
    };
    triggerInviteSecretCode(payload)
      .then(() => {
        Toastr.showToastr({
          type: "success",
          message: "Secret code sent!",
        });
      })
      .catch(err => {
        console.error(
          `OrgListing => _selectedDomain => triggerInviteSecretCode => ${err}`,
        );
        setState(prev => ({
          ...prev,
          showJoinBtnLoader: true,
          errMsg: "Something went wrong. Please try again!",
        }));
      });
  };

  const _onGoogleSuccess = response => {
    const {
      profileObj: { givenName: fname, familyName: lname, email: gEmail } = {},
      tokenObj: { id_token: googleToken } = {},
    } = response;

    // checking if the both entered 'email' and
    // google authenticated 'gEmail' are same or not
    if (email !== gEmail) {
      setState(prev => ({
        ...prev,
        errMsg: "Emails mismatched. Please try again!",
      }));
    } else {
      setState(prev => ({
        ...prev,
        fname,
        lname,
        googleToken,
        showSignUpForm: true,
        preference: "g",
      }));
    }

    // disconnecting(signout) the google sign
    const auth2 = window.gapi.auth2.getAuthInstance();
    if (auth2 != null) {
      auth2.signOut().then(auth2.disconnect());
    }
  };

  const _onGoogleFailure = googleError => {
    /**
     * Google Auth Button may fail in the following cases with the respective error codes:
     * 1. "idpiframe_initialization_failed" :=> Initialization of the Google Auth API failed
     *                                          (this will occur if a client doesn't have third party cookies enabled),
     * 2. "popup_closed_by_user"            :=> The user closed the popup before finishing the sign in flow,
     * 3. "access_denied"                   :=> The user denied the permission to the scopes required,
     * 4. "immediate_failed"                :=> No user could be automatically selected without prompting the consent flow.
     */

    if (
      googleError &&
      googleError.error === "idpiframe_initialization_failed"
    ) {
      setState(prev => ({
        ...prev,
        errMsg:
          "To sign in via Google email, please enable cookies in your browser settings.",
      }));
    } else {
      setState(prev => ({
        ...prev,
        errMsg: "Something went wrong. Please try again!",
      }));
    }
  };

  const _showSignUpForm = () => {
    const formData = {
      fname: state.fname,
      lname: state.lname,
      email,
      googleToken: state.googleToken,
      utmTerm: { utm_term: "direct" },
      oneClickInviteToken: state.inviteCode,
      subDomain: selectedDomain,
    };

    return (
      <SignupQesWrapper
        formData={formData}
        closeView={() => {}}
        signupMethod={state.preference || domain.preference}
        suggestion={suggestion}
        isInviteSignup
        isOneClickInvite
      />
    );
  };

  return (
    <Fragment>
      <div className={signIn.headerText}>Join Org {domain.sub_domain}</div>
      <div className={signIn.secretCodeLabel}>
        {/**
        We have sent a secret code to your email. Enter that here to join the
        account you&apos;ve been invited to. 
       */}
        Invited by {domain.invited_by}
      </div>

      {state.errMsg && (
        <div className={signIn.errorMessage}>
          <span className={signIn.errorMessageIcon}>
            <FontAwesomeIcon icon={["far", "times-circle"]} />
          </span>
          {state.errMsg}
        </div>
      )}

      {isDomainPreferenceSaml ? (
        <SAMLAuth
          title="Signup with SAML-SSO"
          usedFor="SIGNUP"
          userName={email}
        />
      ) : (
        <Fragment>
          <div className={signIn.googleBtn}>
            <GoogleAuth
              btnTitle="Sign up with Google"
              onSuccess={res => _onGoogleSuccess(res)}
              onFailure={err => _onGoogleFailure(err)}
            />
          </div>

          <div className={signIn.googleSeparatorWrapper}>
            <span className={signIn.googleSeparator}>OR</span>
          </div>
          <div className={signIn.formSection}>
            <span className={signIn.fieldLabel}>Work Email</span>
            <Input
              data-test="signin-work-email"
              className={signIn.formControl}
              id="signin-email-field"
              type="email"
              placeholder="Enter your work email"
              name="email"
              value={email}
              disabled
            />
          </div>
          <div className={signIn.formSection}>
            <span className={signIn.fieldLabel}>
              Enter secret code received in email.
            </span>
            <Input
              data-test="pending-invite-form-secret-code"
              className={signIn.secretCodeInput}
              type="text"
              placeholder=""
              name="secret-code"
              value={state.secretCode}
              onChange={e => _onSecretCodeInputChange(e)}
              autoFocus
            />
          </div>
          <div>
            <Button
              data-test="signin-continue"
              className={signIn.authBtn}
              id="signin-email-continue-btn"
              isLoading={false}
              onClick={() => _validateSecretCode()}>
              Submit
            </Button>

            <div className={signIn.resendSecretCode}>
              Didn&apos;t get the email?{" "}
              <span
                onClick={() => _reTriggerInviteSecretCode()}
                onKeyPress={() => {}}
                role="button"
                tabIndex="0">
                Click here to re-send
              </span>{" "}
              and check your spam folder too.
            </div>
          </div>
        </Fragment>
      )}

      {state.showSignUpForm && _showSignUpForm()}

      {/** FOOTER SECTION */}
      <section>
        <Button
          data-test="signin-go-back"
          className={signIn.goBackBtn}
          onClick={() => _onGoBackClick()}>
          <FontAwesomeIcon
            icon={["far", "arrow-left"]}
            className={signIn.goBackBtnIcon}
          />
          Go back
        </Button>
      </section>
    </Fragment>
  );
}

PendingInviteForm.defaultProps = {};

PendingInviteForm.propTypes = {
  data: PropTypes.shape({
    email: PropTypes.string.isRequired,
    invitedDomains: PropTypes.arrayOf(
      PropTypes.shape({
        sub_domain: PropTypes.string.isRequired,
        preference: PropTypes.string.isRequired,
      }),
    ).isRequired,
    selectedDomain: PropTypes.string.isRequired,
    suggestion: PropTypes.shape({}),
  }).isRequired,

  toggleView: PropTypes.func.isRequired,
};

export default PendingInviteForm;
